import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import FDCSideRail from "../../../components/Common/FDCSideRail";
import Pagination from "../../../components/Common/Pagination";
import Loader from "../../../components/Common/Loader";
import { commonService } from "../../../services/commonService";
import { isCXUser } from "../../../utils/permissions";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import { ROUTES } from "../../../utils/constants";
import "../../../assets/styles/Workflow.css";

// Customer Experience-only - not a role (see isCXUser), so this page gates
// itself the same way the backend gates the underlying endpoints: a
// hardcoded email allowlist, not a permission check. Anyone else landing
// here (typed URL, stale bookmark) sees a plain "not available" message,
// same spirit as ProtectedRoute's own denial screens elsewhere.
const CustomerExperiencePage: React.FC = () => {
  const navigate = useNavigate();
  const [railOpen, setRailOpen] = useState(false);
  const [scope, setScope] = useState<"flagged" | "all">("flagged");
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pageDetail, setPageDetail] = useState({ page: 1, total_pages: 0, total_records: 0, page_size: 20 });

  const fetchData = async (newPage: number, search: string) => {
    setLoading(true);
    setError(null);
    try {
      const payload: any = { page: newPage, page_size: pageDetail.page_size, search };
      if (scope === "flagged") payload.grievance_only = true;
      const response = await commonService.fdcList(payload);
      setRows(response?.data || []);
      setPageDetail({
        page: response.page,
        total_pages: response.total_pages,
        total_records: response.total,
        page_size: response.page_size,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCXUser()) fetchData(1, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  if (!isCXUser()) {
    return (
      <MainLayout>
        <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
          Customer Experience isn't available for your account.
        </div>
      </MainLayout>
    );
  }

  const toggleFlag = async (row: any) => {
    setBusyId(row.id);
    setError(null);
    try {
      if (row.grievance_flagged) {
        await commonService.fdcUnflagGrievance(row.id);
      } else {
        await commonService.fdcFlagGrievance(row.id);
      }
      await fetchData(pageDetail.page || 1, searchText);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update the Grievance flag.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <MainLayout>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <FDCSideRail mobileOpen={railOpen} onCloseMobile={() => setRailOpen(false)} />
        <div className="rde-downloads-page" style={{ backgroundColor: "#f5f5f5", flex: 1, overflowY: "auto", padding: "26px 30px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <button onClick={() => setRailOpen(true)} className="wf-rail-toggle" aria-label="Open menu">☰</button>
            <button onClick={() => navigate(ROUTES.DASHBOARD)} className="regular-rdc-back-button" style={{ margin: 0 }}>
              <BackIcon />
              Back
            </button>
          </div>

          <div className="wf-section-title" style={{ marginBottom: 4 }}>
            <h3>Customer Experience</h3>
          </div>
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 18 }}>
            Customers currently flagged with a Grievance are excluded from the 90-day data-update email across every
            project they have - not just the one that raised the flag. Resolve it here once the customer has confirmed.
          </div>

          <div className="wf-searchbar" style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div className="wf-radiorow">
              <label>
                <input type="radio" checked={scope === "flagged"} onChange={() => setScope("flagged")} /> Flagged only
              </label>
              <label>
                <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} /> All projects (to flag one)
              </label>
            </div>
            <input
              type="text"
              placeholder="Search project, brand, CRM ID…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 240 }}
              onKeyDown={(e) => { if (e.key === "Enter") fetchData(1, searchText); }}
            />
            <button className="wf-btn" onClick={() => fetchData(1, searchText)}>Search</button>
          </div>

          {error && <div style={{ color: "#ef4444", marginBottom: 12 }}>{error}</div>}

          {loading ? (
            <Loader text="Loading..." />
          ) : (
            <div className="wf-card wf-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Brand</th>
                    <th>CRM ID</th>
                    <th>Record Type</th>
                    <th>Customer Email</th>
                    <th>Created</th>
                    <th>Grievance</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={8}><div className="wf-empty-hint">
                      {scope === "flagged" ? "No customers are currently flagged." : "No projects found."}
                    </div></td></tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td data-label="Project">
                          <div style={{ fontWeight: 500, fontSize: 12.5 }}>{row.summary}</div>
                          <div className="wf-mono" style={{ fontSize: 11, color: "#9ca3af" }}>{row.projectid}</div>
                        </td>
                        <td data-label="Brand">{row.brand_name}</td>
                        <td data-label="CRM ID" className="wf-mono">{row.crm_id}</td>
                        <td data-label="Record Type">{row.record_type}</td>
                        <td data-label="Customer Email">{row.customer_email || <span style={{ color: "#9ca3af" }}>—</span>}</td>
                        <td data-label="Created">{row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB", { timeZone: "America/Los_Angeles" }) : "—"}</td>
                        <td data-label="Grievance">
                          {row.grievance_flagged ? (
                            <span className="wf-chip2 wf-chip2-crit">⚠ Flagged</span>
                          ) : (
                            <span className="wf-chip2 wf-chip2-neutral">Clear</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              className={`wf-btn ${row.grievance_flagged ? "" : "wf-btn-primary"}`}
                              disabled={busyId === row.id}
                              onClick={() => toggleFlag(row)}
                            >
                              {busyId === row.id ? "Saving…" : row.grievance_flagged ? "Remove flag" : "Flag"}
                            </button>
                            <button
                              className="wf-btn"
                              onClick={() => window.open(`/data-centers/fdc/projects/${row.id}`, "_blank")}
                            >
                              View →
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {pageDetail.total_pages > 1 && (
            <Pagination
              page={pageDetail.page || 1}
              total_pages={pageDetail.total_pages}
              total_records={pageDetail.total_records}
              page_size={pageDetail.page_size}
              onPageChange={(newPage) => fetchData(newPage, searchText)}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default CustomerExperiencePage;
